import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import { CloseOutlined } from '@ant-design/icons';
import { Button, Col, Row, Select, Switch } from 'antd';

import ModalActions from 'components/ModalActions';
import { useNutror } from 'v2/hooks/useNutror';

interface IProps {
  isModal: boolean;
  open?: boolean;
  onClose?: any;
  onOk: any;
  nutror?: any;
}

const Nutror: React.FC<IProps> = ({ isModal, open, onClose, onOk, nutror }) => {
  const [directAccess, setDirectAccess] = useState(false);
  const [selected, setSelected] = useState<any>(null);
  const [inputSelected, setInputSelected] = useState<string | undefined>(undefined);
  const { courses, packages } = useNutror();

  useEffect(() => {
    if (!nutror) {
      setSelected(null);
      setInputSelected(undefined);
      setDirectAccess(false);
      return;
    }

    const hash = nutror.nutror_hash || nutror.hash;
    setSelected(nutror);
    setInputSelected(hash || undefined);
    setDirectAccess(!!nutror.direct_access);
  }, [nutror, open]);

  const handleChange = useCallback(
    (hash: string) => {
      let newSelected: any;
      const selectedCourse = courses?.find(item => item.hash === hash);
      const selectedPackage = packages?.find(item => item.hash === hash);

      if (selectedCourse) {
        newSelected = { ...selectedCourse };
        newSelected.nutror_id = selectedCourse.id;
        newSelected.type = 'C';
      } else if (selectedPackage) {
        newSelected = { ...selectedPackage };
        newSelected.nutror_id = selectedPackage.id;
        newSelected.type = 'P';
      } else {
        toast.error('Ocorreu um erro ao tentar selecionar o curso ou pacote do nutror');
        return;
      }

      newSelected.nutror_hash = hash;
      newSelected.method_id = '24';
      newSelected.nutror_name = newSelected.title;
      setSelected(newSelected);
      setInputSelected(hash);
    },
    [courses, packages]
  );

  const handleOk = useCallback(() => {
    if (selected) {
      if (onOk) {
        const currentHash = selected.nutror_hash || selected.hash;
        const savedHash = nutror?.nutror_hash || nutror?.hash;

        if (nutror && currentHash === savedHash && directAccess === nutror.direct_access) return onClose();

        selected.new = true;
        selected.direct_access = directAccess;
        selected.nutror_hash = currentHash;
        onOk(selected, nutror);
        if (!nutror) toast.success(`Item ${selected.title || selected.nutror_name} adicionado com sucesso!`);
        setSelected(null);
        setInputSelected(undefined);
      }
    } else {
      toast.warn('Por favor selecione um curso ou pacote');
    }
  }, [selected, directAccess, onOk, nutror, onClose]);

  const courseOptions = useMemo(
    () =>
      courses?.length
        ? courses.map(c => ({ label: c.title, value: c.hash }))
        : [{ label: 'Não há cursos cadastrados', value: '__empty_courses', disabled: true }],
    [courses]
  );

  const packageOptions = useMemo(
    () =>
      packages?.length
        ? packages.map(p => ({ label: p.title, value: p.hash }))
        : [{ label: 'Não há pacotes cadastrados', value: '__empty_packages', disabled: true }],
    [packages]
  );

  const content = (
    <form>
      <Row justify='center'>
        <Col span={24}>
          <label className='mb-1 block text-xs'>Selecione um curso ou pacote</label>
          <Select
            className='w-full'
            value={inputSelected}
            onChange={handleChange}
            placeholder='Selecione um curso ou pacote'
            options={[
              { label: 'Cursos', options: courseOptions },
              { label: 'Pacotes', options: packageOptions }
            ]}
          />
        </Col>
        <Col span={24} className='mt-4'>
          <label className='flex items-center gap-2'>
            <Switch checked={directAccess} onChange={checked => setDirectAccess(checked)} />
            Habilitar acesso direto na página de obrigado.
          </label>
        </Col>

        <Col span={24} className='mt-4'>
          <strong>Novo por aqui? Crie um curso no Nutror</strong>
          <p>
            O Nutror é nossa plataforma de membros, com ele você pode criar cursos e grupos de assinatura. Acesse o{' '}
            <a href='https://my.nutror.com/cursos' target='_blank' rel='noopener noreferrer'>
              link
            </a>{' '}
            para criar o curso!
          </p>
        </Col>
      </Row>
    </form>
  );

  return (
    <div>
      {isModal ? (
        <ModalActions
          buttonOk='Salvar'
          title='Entrega de Conteúdo'
          open={open}
          onClose={onClose}
          onOk={handleOk}
          maskClosable={false}
        >
          {content}
        </ModalActions>
      ) : (
        <div className='rounded-sm bg-white shadow-sm'>
          <div className='flex items-center justify-between border-b p-4'>
            <h3 className='font-medium'>Entrega de Conteúdo</h3>
            <Button type='text' icon={<CloseOutlined />} />
          </div>
          <div className='p-4'>{content}</div>
        </div>
      )}
    </div>
  );
};

export default Nutror;
